/* eslint-disable import/prefer-default-export */
import config from "@Config/index";
import LangChinaFlag from "@Icons/LangChinaFlag";
import LangEnglishUKFlag from "@Icons/LangEnglishUKFlag";
import LangFranceFlag from "@Icons/LangFranceFlag";
import LangGermanyFlag from "@Icons/LangGermanyFlag";
import LangSpainFlag from "@Icons/LangSpainFlag";
import LangThailandFlag from "@Icons/LangThailandFlag";
import LangTurkeyFlag from "@Icons/LangTurkeyFlag";
import LangVietnamFlag from "@Icons/LangVietnamFlag";

export interface LanguageConfig {
  name: string;
  code: string;
  nativeLabel: string;
  domains: string[];
  flag: any;
  shortDisplayText: string;
  hasRegionalBlog?: boolean;
  /**
   * Restrict this locale to the global `.com` build — excluded from the China (`.cn`) and
   * whitelabel partner builds. Those are separate products; a locale nobody scoped for them
   * must not appear in their language pickers or generate routes there.
   */
  globalOnly?: boolean;
  /**
   * Restrict this locale to the China (`.cn`) build — excluded from global and whitelabel.
   */
  chinaOnly?: boolean;
}

const ALL_LANGUAGES: LanguageConfig[] = [
  {
    name: "english",
    nativeLabel: "English",
    code: "en",
    domains: config.isProduction
      ? ["amberstudent.com", "hk.amberstudent.com"]
      : ["staging-amber.com", "hk.staging-amber.com", "localhost"],
    flag: LangEnglishUKFlag,
    shortDisplayText: "En",
  },
  {
    name: "chinese-simplified",
    nativeLabel: "简体中文",
    code: "zh",
    domains: config.isProduction
      ? ["amberstudent.cn"]
      : ["staging-ali.amberstudent.cn", "china.staging-amber.com"],
    flag: LangChinaFlag,
    shortDisplayText: "中文",
  },
  {
    name: "french",
    nativeLabel: "French",
    code: "fr",
    domains: config.isProduction
      ? ["amberstudent.com", "hk.amberstudent.com"]
      : ["staging-amber.com", "hk.staging-amber.com"],
    flag: LangFranceFlag,
    shortDisplayText: "Fr",
    hasRegionalBlog: true,
  },
  {
    name: "spanish",
    nativeLabel: "Spanish",
    code: "es",
    domains: config.isProduction
      ? ["amberstudent.com", "hk.amberstudent.com"]
      : ["staging-amber.com", "hk.staging-amber.com"],
    flag: LangSpainFlag,

    shortDisplayText: "Es",
    hasRegionalBlog: true,
  },
  {
    name: "german",
    nativeLabel: "German",
    code: "de",
    domains: config.isProduction
      ? ["amberstudent.com", "hk.amberstudent.com"]
      : ["staging-amber.com", "hk.staging-amber.com", "localhost"],
    flag: LangGermanyFlag,
    shortDisplayText: "De",
    hasRegionalBlog: true,
  },
  {
    name: "chinese-traditional-tw",
    nativeLabel: "繁體中文（台灣）",
    code: "zhtw",
    domains: config.isProduction
      ? ["amberstudent.cn"]
      : ["staging-ali.amberstudent.cn", "china.staging-amber.com"],
    flag: LangChinaFlag,
    shortDisplayText: "繁中",
    chinaOnly: true,
  },
  {
    name: "chinese-traditional-hk",
    nativeLabel: "繁體中文（香港）",
    code: "zhhk",
    domains: config.isProduction
      ? ["amberstudent.cn"]
      : ["staging-ali.amberstudent.cn", "china.staging-amber.com"],
    flag: LangChinaFlag,
    shortDisplayText: "繁中HK",
    chinaOnly: true,
  },
  // ---------------------------------------------------------------------------
  // NOINDEX LOCALES — vi / th / tr
  //
  // Launched DELIBERATELY INVISIBLE to search engines, and NOT on the same footing as
  // fr/es/de. For these three there must be:
  //   - no sitemap             (s.base allowlists `%w[fr es de]` and 404s the rest —
  //                             do NOT add these codes there)
  //   - no robots.txt entry    (s.web robots.ejs declares /de|/fr|/es sitemaps —
  //                             do NOT add /vi|/th|/tr lines)
  //   - no hreflang            (HREFLANG_EXCLUDED_LANGS in utils/generateHreflangUrls.ts)
  //   - no crawlable SSR link  (SSR_HIDDEN_LANGS in utils/languageCore.ts)
  //
  // Do NOT "complete the rollout" by copying fr/es/de. Making these indexable is a separate
  // decision requiring SEO sign-off.
  // ---------------------------------------------------------------------------
  {
    name: "vietnamese",
    nativeLabel: "Tiếng Việt",
    code: "vi",
    domains: config.isProduction
      ? ["amberstudent.com", "hk.amberstudent.com"]
      : ["staging-amber.com", "hk.staging-amber.com", "localhost"],
    flag: LangVietnamFlag,
    shortDisplayText: "Vi",
    globalOnly: true,
  },
  {
    name: "thai",
    nativeLabel: "ไทย",
    code: "th",
    domains: config.isProduction
      ? ["amberstudent.com", "hk.amberstudent.com"]
      : ["staging-amber.com", "hk.staging-amber.com", "localhost"],
    flag: LangThailandFlag,
    shortDisplayText: "Th",
    globalOnly: true,
  },
  {
    name: "turkish",
    nativeLabel: "Türkçe",
    code: "tr",
    domains: config.isProduction
      ? ["amberstudent.com", "hk.amberstudent.com"]
      : ["staging-amber.com", "hk.staging-amber.com", "localhost"],
    flag: LangTurkeyFlag,
    shortDisplayText: "Tr",
    globalOnly: true,
  },
];

/**
 * The locale set for THIS build.
 *
 * Filtered at source rather than in each consumer, so route generation
 * (`LANGUAGE_PREFIX_PATTERN`), the language pickers, `getLanguageFromHostname` and the
 * hreflang/canonical helpers can never disagree about which locales exist. On the China and
 * whitelabel builds a `globalOnly` locale is absent entirely — its routes are never generated,
 * so `/vi/...` 404s there, which is intended. A `chinaOnly` locale exists on the China build only.
 */
export const languagesForAmberUserWebsite: LanguageConfig[] = ALL_LANGUAGES.filter(
  (lang) =>
    !(lang.globalOnly && (config.IS_CHINA_APP || config.IS_WHITELABEL_APP)) &&
    !(lang.chinaOnly && !config.IS_CHINA_APP),
);

export const SUPPORTED_LANG_VALUES = languagesForAmberUserWebsite.map((lang) => lang.code);

/** Segments that may appear as the first path component in a language-prefixed route. */
export const LANGUAGE_URL_PREFIXES = SUPPORTED_LANG_VALUES.filter(
  (code) => code !== "en" && code !== "zh",
);

/** Languages offered in the `.cn` header selector (P1D-5521). */
export const CHINA_HEADER_LANGUAGE_CODES = ["zh", "zhtw", "zhhk"] as const;

export const REGIONAL_BLOG_LANG_VALUES = languagesForAmberUserWebsite
  .filter((lang) => lang.hasRegionalBlog)
  .map((lang) => lang.code);
